import { useRef, useState, useEffect } from "react";

import { css, keyframes } from "@compiled/react";
import { flattenValues } from "@uniformdev/canvas";
import { useIntl } from "react-intl";

import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { UniformColumnLayout } from "../../column-layout";

interface VideoCarouselSlide {
    team: string;
    mobileVideoSrc: string;
    tabletVideoSrc: string;
    desktopVideoSrc: string;
    mobilePoster?: string;
    tabletPoster?: string;
    desktopPoster?: string;
    ariaLabel?: string;
}

export interface UniformVideoCarouselProps {
    defaultHeading: string;
    defaultSubheading?: string;
    videoCarousel: VideoCarouselSlide[];
    primaryCtaLabel?: string;
    primaryCtaHref?: string;
    headingTag?: "h1" | "h2";
    flattenData?: boolean;
}

const UniformVideoCarousel = ({
    videoCarousel,
    flattenData = true
}: UniformVideoCarouselProps) => {
    const videoCarouselData = flattenData
        ? (flattenValues(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              videoCarousel as any
          ) as VideoCarouselSlide[])
        : videoCarousel;

    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const videoContainerRef = useRef<HTMLDivElement | null>(null);

    const [current, setCurrent] = useState(0);
    const currentRef = useRef(current);
    const [playing, setPlaying] = useState(true);
    const [durations, setDurations] = useState<number[]>(
        Array(videoCarouselData.length).fill(0)
    );

    const [fillBarResetKey, setFillBarResetKey] = useState(0);

    // @ts-ignore
    const prevViewportWidth = useRef(
        typeof window !== "undefined" ? window.innerWidth : 0
    );
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 0
    );

    useEffect(() => {
        currentRef.current = current;
    }, [current]);

    // On-load - set up video durations
    useEffect(() => {
        // Video durations
        videoCarouselData.forEach((_, idx) => {
            const video = videoRefs.current[idx];
            if (!video) return;
            if (video.readyState >= 1) {
                setDurations((durs) => {
                    const copy = [...durs];
                    copy[idx] = video.duration;
                    return copy;
                });
            } else {
                video.onloadedmetadata = () => {
                    setDurations((durs) => {
                        const copy = [...durs];
                        copy[idx] = video.duration;
                        return copy;
                    });
                };
            }
        });
    }, [videoCarouselData]);

    // Play next video on end, looping
    const handleEnded = () => {
        const currVideo = videoRefs.current[current];
        if (currVideo) {
            currVideo.pause();
            currVideo.currentTime = 0;
        }

        setCurrent((prev) => (prev + 1) % videoCarouselData.length);
    };

    // Pause/play current video
    useEffect(() => {
        const video = videoRefs.current[current];
        if (video) {
            if (playing) {
                if (video.ended) {
                    video.currentTime = 0;
                }
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                video.play().catch(() => {});
            } else {
                video.pause();
            }
        }
    }, [playing, current]);

    // Pause/play toggle
    const togglePlay = () => {
        setPlaying((p) => !p);
    };

    // Jump to a specific video and start from beginning
    const handleTeamClick = (idx: number) => {
        if (idx === current) return;

        const currVideo = videoRefs.current[current];
        if (currVideo) {
            currVideo.pause();
            currVideo.currentTime = 0;
        }

        setCurrent(idx);
        setPlaying(true);
    };

    const currentDuration = durations[current] || 10;

    // Responsive asset type
    useEffect(() => {
        let resizeTimeout: number | undefined;
        const breakpoints = [768, 1024];
        // Store previous width outside the handler so it persists
        const prevViewportWidth = { current: window.innerWidth };
        function crossedBreakpoint(prev: number, curr: number) {
            return breakpoints.some(
                (bp) => (prev < bp && curr >= bp) || (prev >= bp && curr < bp)
            );
        }
        const handleResizeEnd = () => {
            const currWidth = window.innerWidth;
            if (crossedBreakpoint(prevViewportWidth.current, currWidth)) {
                setFillBarResetKey((k) => k + 1);
            }
            prevViewportWidth.current = currWidth;
        };
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
            if (resizeTimeout !== undefined) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = window.setTimeout(handleResizeEnd, 300);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (resizeTimeout !== undefined) {
                clearTimeout(resizeTimeout);
            }
        };
    }, []);

    const assetType =
        viewportWidth >= 1024
            ? "desktop"
            : viewportWidth >= 768
            ? "tablet"
            : "mobile";

    const intl = useIntl();

    return (
        <div css={containerCss}>
            <div
                css={[videoContainerCss, videoDimensionsCss]}
                ref={videoContainerRef}
            >
                <div css={videoCss}>
                    {videoCarouselData.map(
                        (
                            {
                                team,
                                mobileVideoSrc,
                                tabletVideoSrc,
                                desktopVideoSrc,
                                mobilePoster,
                                tabletPoster,
                                desktopPoster,
                                ariaLabel
                            },
                            idx
                        ) => (
                            <video
                                key={team}
                                ref={(el) => (videoRefs.current[idx] = el)}
                                src={
                                    assetType === "desktop"
                                        ? desktopVideoSrc
                                        : assetType === "tablet"
                                        ? tabletVideoSrc
                                        : mobileVideoSrc
                                }
                                muted={true}
                                playsInline={true}
                                css={[
                                    videoDimensionsCss,
                                    css({
                                        display: "block",
                                        visibility:
                                            idx === current
                                                ? "visible"
                                                : "hidden",
                                        height: `${
                                            idx === current ? "100%" : 0
                                        } !important`,
                                        objectFit: "cover"
                                    })
                                ]}
                                onEnded={handleEnded}
                                autoPlay={idx === current && playing}
                                controls={false}
                                aria-label={ariaLabel}
                                preload="auto"
                                poster={
                                    (assetType === "desktop"
                                        ? desktopPoster
                                        : assetType === "tablet"
                                        ? tabletPoster
                                        : mobilePoster) ?? undefined
                                }
                            />
                        )
                    )}
                </div>
            </div>
            <UniformColumnLayout layout="12">
                <div css={controlContainerCss}>
                    <button css={buttonCss} onClick={togglePlay}>
                        <div css={buttonCirlceCss}>
                            {playing ? (
                                <VideoPauseOverlayIcon
                                    label={intl.formatMessage({
                                        id: "rovo-hero.pause",
                                        defaultMessage: "Pause"
                                    })}
                                />
                            ) : (
                                <VideoPlayOverlayIcon
                                    label={intl.formatMessage({
                                        id: "rovo-hero.play",
                                        defaultMessage: "Play"
                                    })}
                                />
                            )}
                        </div>
                    </button>
                    <div css={barContainerCss}>
                        {videoCarouselData.map(({ team }, idx) => (
                            <button
                                key={team}
                                onClick={() => handleTeamClick(idx)}
                                css={[
                                    barbuttonCss,
                                    css({
                                        color:
                                            idx === current
                                                ? wacToken("color.primary.blue")
                                                : wacToken(
                                                      "color.neutral.light800"
                                                  ),

                                        ["&:hover"]: {
                                            cursor:
                                                idx === current
                                                    ? "auto"
                                                    : "pointer"
                                        }
                                    })
                                ]}
                            >
                                <div
                                    css={[
                                        barCss,
                                        idx === 0 && firstBarCss,
                                        idx === videoCarouselData.length - 1 &&
                                            lastBarCss
                                    ]}
                                >
                                    {idx === current && (
                                        <div
                                            key={fillBarResetKey}
                                            className="fillBar"
                                            css={[
                                                fillBarCss,
                                                css({
                                                    animation: `${fill} ${currentDuration}s linear forwards`,
                                                    animationPlayState: playing
                                                        ? "running"
                                                        : "paused"
                                                })
                                            ]}
                                        />
                                    )}
                                </div>
                                {team}
                            </button>
                        ))}
                    </div>
                </div>
            </UniformColumnLayout>
        </div>
    );
};

export default UniformVideoCarousel;

const containerCss = css({
    overflow: "hidden"
});

const videoContainerCss = css({
    position: "relative",
    insetInlineStart: "50%",
    transform: "translateX(-50%)"
});

const videoDimensionsCss = css({
    width: "100%",
    height: "312px",

    [media.only.sm]: {
        height: "324px"
    },

    [media.below.sm]: {
        height: "164px"
    }
});

const videoCss = css({
    width: "100%",
    height: "100%",
    display: "block",
    textAlign: "center",
    position: "relative",
    overflow: "hidden"
});

const controlContainerCss = css({
    marginBlockStart: token("space.600"),
    marginInline: "auto",
    width: "100%",
    maxWidth: "768px",
    gap: token("space.150"),

    display: "grid",
    gridTemplateColumns: "1fr min-content",
    minWidth: 0,

    [media.below.sm]: {
        marginBlockStart: token("space.150"),
        gridTemplateColumns: "auto",
        justifyItems: "end"
    }
});

const barContainerCss = css({
    display: "flex",
    alignItems: "flex-start",
    gap: token("space.150"),
    width: "100%",
    minWidth: 0,
    overflow: "hidden"
});

const buttonCss = css({
    height: "44px",
    width: "44px",
    margin: "-6px",

    backgroundColor: "transparent",
    border: "none",
    borderRadius: "100%",

    gridRow: "1",
    gridColumn: "2",

    ["&:hover"]: {
        cursor: "pointer"
    },

    [media.below.sm]: {
        gridRow: "1",
        gridColumn: "1"
    }
});

const buttonCirlceCss = css({
    height: "32px",
    width: "32px",
    backgroundColor: wacToken("color.neutral.dark1000"),
    border: "none",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    ["&:hover"]: {
        cursor: "pointer",
        backgroundColor: wacToken("color.neutral.dark800")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        color: `${wacToken("color.neutral.dark300")} !important`
    }
});

const firstBarCss = css({
    borderRadius: "10px 0 0 10px"
});

const lastBarCss = css({
    borderRadius: "0 10px 10px 0"
});

const barbuttonCss = css({
    background: "none",
    border: "none",
    padding: 0,
    width: "100%",
    minWidth: 0,
    flexShrink: 1,
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: "1.5rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
});

const barCss = css({
    height: "8px",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: wacToken("color.neutral.light300"),
    marginBlock: `${token("space.150")} ${token("space.100")}`
});

const fillBarCss = css({
    height: "100%",
    width: "100%",
    backgroundColor: wacToken("color.primary.blue"),
    position: "absolute",
    insetInlineStart: 0,
    insetBlockStart: 0
});

const fill = keyframes({
    from: { width: "0%" },
    to: { width: "100%" }
});
